import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Space, message, Card, List, Radio, Spin, Alert } from 'antd';
import { DownloadOutlined, FileOutlined, FolderOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import api from '../services/api';
import { createAutoAccessGate, getAccessPayload } from '../utils/shareSecurity';
import './FileAccess.css';

const { Title, Paragraph, Text } = Typography;

const FileGroupAccess = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [filesCount, setFilesCount] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [downloadOption, setDownloadOption] = useState('zip');
  const [downloading, setDownloading] = useState(false);
  const [showManualDownload, setShowManualDownload] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(true);
  const [form] = Form.useForm();
  const autoAccessGate = useRef(null);

  if (!autoAccessGate.current) {
    autoAccessGate.current = createAutoAccessGate();
  }

  useEffect(() => {
    checkFileGroupStatus();
  }, [groupId]);

  const checkFileGroupStatus = async () => {
    try {
      const response = await api.get(`/api/file-groups/status/${groupId}`);
      if (response.data.status === 'available') {
        const nextRequireEmail = response.data.requireEmail || false;
        const nextPasswordRequired = response.data.passwordRequired !== false;
        setFilesCount(response.data.filesCount);
        setTotalSize(response.data.totalSize);
        setRequireEmail(nextRequireEmail);
        setPasswordRequired(nextPasswordRequired);
        setNotFound(false);

        if (autoAccessGate.current.shouldStart({
          shareId: groupId,
          passwordRequired: nextPasswordRequired,
          requireEmail: nextRequireEmail,
        })) {
          await handleAccess({}, { passwordRequired: nextPasswordRequired });
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else if (error.response?.status === 410) {
        setNotFound(true);
        message.error('This file group has expired and been deleted');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccess = async (values, options = {}) => {
    setChecking(true);
    try {
      const response = await api.post(
        `/api/file-groups/access/${groupId}`,
        getAccessPayload({
          passwordRequired: options.passwordRequired ?? passwordRequired,
          values,
        }),
      );

      setFiles(response.data.files);
      setTotalSize(response.data.totalSize);
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response?.data?.error || 'Invalid request. Please check your input.');
      } else if (error.response?.status === 401) {
        message.error('Invalid password');
      } else if (error.response?.status === 404) {
        message.error('File group not found');
        setNotFound(true);
      } else if (error.response?.status === 410) {
        message.error('This file group has expired and been deleted');
        setNotFound(true);
      } else {
        message.error('Failed to access file group');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleDownload = async () => {
    if (downloadOption === 'zip') {
      handleDownloadZip();
    } else {
      handleDownloadIndividual();
    }
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const response = await api.post(`/api/file-groups/download-zip/${groupId}`,
        getAccessPayload({
          passwordRequired,
          values: form.getFieldsValue(['password', 'email']),
        }),
        {
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Download progress: ${percentCompleted}%`);
          }
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `files-${groupId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Download started successfully');
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download files');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadIndividual = async () => {
    setDownloading(true);
    try {
      message.info(`Starting download of ${files.length} files. Your browser may ask for permission to download multiple files.`);
      
      // Create a delay between downloads to avoid browser blocking
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create a promise that resolves when the download starts
        await new Promise((resolve) => {
          const link = document.createElement('a');
          link.href = file.downloadUrl;
          link.download = file.originalName;
          link.style.display = 'none';
          
          // Add to body, click, and remove
          document.body.appendChild(link);
          
          // Use setTimeout to ensure each download is initiated separately
          setTimeout(() => {
            link.click();
            document.body.removeChild(link);
            resolve();
          }, i * 500); // 500ms delay between each file
        });
      }
      
      message.success(`Initiated download of ${files.length} files. Check your downloads folder.`);
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download files');
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="file-access-page">
        <Card className="access-card">
          <Spin size="large" />
        </Card>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="file-access-page">
        <Card className="access-card">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={3}>File Group Not Found</Title>
            <Paragraph>
              The file group you're looking for doesn't exist or has been deleted.
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="file-access-page">
      <Card className="access-card">
        <div className="access-header">
          <FolderOutlined className="file-icon" />
          <Title level={2}>File Group Access</Title>
          <Paragraph>
            This link contains {filesCount} files ({formatFileSize(totalSize)})
          </Paragraph>
        </div>

        {!hasAccess ? (
          <Form
            form={form}
            onFinish={handleAccess}
            layout="vertical"
            className="access-form"
          >
            {requireEmail && (
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Email is required to download these files' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter your email address"
                  size="large"
                />
              </Form.Item>
            )}
            {passwordRequired && (
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter the password' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter password to access files"
                  size="large"
                />
              </Form.Item>
            )}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={checking}
                size="large"
                block
              >
                {passwordRequired ? 'Access Files' : 'Continue to Files'}
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div className="file-content">
            <Title level={4}>Files in this group:</Title>
            <List
              dataSource={files}
              renderItem={file => (
                <List.Item
                  actions={showManualDownload ? [
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      href={file.downloadUrl}
                      download={file.originalName}
                      target="_blank"
                    >
                      Download
                    </Button>
                  ] : []}
                >
                  <List.Item.Meta
                    avatar={<FileOutlined />}
                    title={file.originalName}
                    description={formatFileSize(file.size)}
                  />
                </List.Item>
              )}
              style={{ marginBottom: 24 }}
            />

            <Card>
              <Title level={5}>Download Options</Title>
              <Radio.Group 
                value={downloadOption} 
                onChange={(e) => setDownloadOption(e.target.value)}
                style={{ marginBottom: 16 }}
              >
                <Space direction="vertical">
                  <Radio value="zip">
                    Download all files as ZIP ({formatFileSize(totalSize)})
                  </Radio>
                  <Radio value="individual">
                    Download files individually ({files.length} files)
                  </Radio>
                </Space>
              </Radio.Group>

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={downloading}
                size="large"
                block
              >
                {downloading ? 'Downloading...' : 
                  downloadOption === 'zip' ? 'Download ZIP' : 'Download All Files'}
              </Button>

              {downloadOption === 'individual' && !showManualDownload && (
                <Alert
                  message="Browser Download Limit"
                  description="Most browsers limit automatic downloads. If not all files download, click below to show individual download links."
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  action={
                    <Button size="small" type="primary" onClick={() => setShowManualDownload(true)}>
                      Show Download Links
                    </Button>
                  }
                />
              )}
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FileGroupAccess;
